'use client'

/**
 * Footer newsletter opt-in — a compact, dark-friendly email + button that
 * submits through the REAL forms pipe. The storefront layout resolves the
 * tenant's email-only `forms.id` from the footer chrome override, fetches the
 * live version (see `apps/storefront/src/lib/footerNewsletterForm.ts`), and
 * passes the resolved `form` descriptor here. On submit we POST to
 * `/api/forms/submit` — identical to a `form_embed` section — so signups land
 * in the Forms tab, create/merge a `contacts` row (tags via the form's routing
 * rules), and queue GHL sync. Honeypot + time-trap come from the signed token.
 *
 * The token is fetched by the BROWSER, not minted during render: it expires
 * after 24h, and this footer is on every page, so a server-minted token would
 * be baked into cacheable HTML and silently expire for every visitor sharing
 * it. See `useFormToken` for the full reasoning.
 *
 * The footer copy (headline / placeholder / button) stays editable via the
 * chrome override; the form definition owns server-side validation + tagging.
 *
 * Inline success state, no page navigation (calm UX).
 */
import { useState } from 'react'
import { Mail, User } from 'lucide-react'
import { useFormToken } from '../content/useFormToken'

/** Server-resolved descriptor for the email-only form this footer submits to. */
export type FooterNewsletterFormConfig = {
  formId: string
  versionId: string
  /** HMAC token pinning formId+versionId+renderedAt + honeypot key.
   *  OPTIONAL and normally absent: the token expires after 24h, so it can't be
   *  minted server-side into HTML the edge caches — the browser fetches its own
   *  from `/api/forms/token` (see `useFormToken`). Still honoured when a
   *  per-request caller supplies one. */
  formToken?: string
  /** Per-render random hidden-field name; bots fill it, humans never see it.
   *  Arrives with the fetched token when not supplied. */
  honeypotKey?: string
  /** The email field's `key` in the form definition (submitted as `data[fieldKey]`). */
  fieldKey: string
  /** An OPTIONAL text field for the subscriber's (first) name, when the form
   *  definition carries one. Only the stacked signup (brand_showcase footer)
   *  renders it; the compact pill variants stay email-only. */
  nameFieldKey?: string
  /** Label/placeholder for the name field (from the form definition). */
  nameFieldLabel?: string
  /** Defaults to `/api/forms/submit`. */
  submitUrl?: string
}

/** Shared submit state machine — one pipeline for the footer form and the
 *  `newsletter_signup` section band. Both POST the same signed-token payload
 *  to `/api/forms/submit`; only the surrounding markup differs. */
function useNewsletterSubmit(form: FooterNewsletterFormConfig, sourcePageSlug: string) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const { pair: fetchedToken, ensureToken } = useFormToken({
    formId: form.formId,
    enabled: !form.formToken,
  })

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (status === 'loading') return
    setStatus('loading')
    try {
      // Wait for the on-mount fetch if it hasn't landed — a tokenless submit is
      // rejected as `invalid_token` and silently dropped, losing a real signup.
      const formToken = form.formToken ?? (await ensureToken())?.formToken ?? fetchedToken?.formToken
      const res = await fetch(form.submitUrl ?? '/api/forms/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formId: form.formId,
          versionId: form.versionId,
          data: {
            [form.fieldKey]: email,
            // Name rides along only when the form defines the field AND the
            // subscriber typed one — the field is optional by contract.
            ...(form.nameFieldKey && name.trim() ? { [form.nameFieldKey]: name.trim() } : {}),
          },
          formToken,
          honeypot,
          sourcePageSlug,
        }),
      })
      setStatus(res.ok ? 'done' : 'error')
    } catch {
      setStatus('error')
    }
  }

  // Server-supplied when a per-request caller passes one, otherwise from the
  // fetched token. The hidden input only renders once it's known — a bot that
  // submits before then has no token either, so it's rejected anyway.
  const honeypotKey = form.honeypotKey ?? fetchedToken?.honeypotKey
  return { email, setEmail, name, setName, honeypot, setHoneypot, honeypotKey, status, onSubmit }
}

export function NewsletterForm({
  headline,
  placeholder,
  ctaLabel,
  form,
}: {
  headline: string
  placeholder: string
  ctaLabel: string
  form: FooterNewsletterFormConfig
}) {
  const { email, setEmail, honeypot, setHoneypot, honeypotKey, status, onSubmit } = useNewsletterSubmit(form, 'footer')

  return (
    <div className="max-w-sm" data-chrome-target="footer:newsletter">
      <h3
        className="font-semibold text-sm uppercase tracking-wide mb-3"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {headline}
      </h3>
      {status === 'done' ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          Thanks — you&rsquo;re on the list.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2">
          {/* Honeypot — visually hidden, off the tab order. Field name is the
              per-render random key the signed token binds; the API checks it
              for non-empty values. */}
          <input
            type="text"
            name={honeypotKey}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="hidden"
          />
          <div className="flex items-stretch rounded-full overflow-hidden bg-white">
            <input
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className="flex-1 min-w-0 px-4 py-2.5 text-sm text-gray-900 bg-white outline-none placeholder:text-gray-400"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-5 text-sm font-semibold text-[var(--color-cta-text,#fff)] bg-[var(--color-brand-primary,var(--color-cta))] hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {status === 'loading' ? '…' : ctaLabel}
            </button>
          </div>
          {status === 'error' && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              Something went wrong — please try again.
            </p>
          )}
        </form>
      )}
    </div>
  )
}

/**
 * Stacked footer signup — the `brand_showcase` footer's newsletter surface.
 * Same submit pipeline as the compact pill form (do not fork), different
 * markup: headline + body copy, an optional first-name input (rendered only
 * when the form definition carries a name field), a full-width email input,
 * and a full-width uppercase CTA. Inputs are translucent (white at low
 * opacity over the variant's dark surface, same treatment as
 * NewsletterBandForm's dark-band inputs) with a leading icon per field —
 * person for the name, envelope for the email.
 */
export function NewsletterStackedForm({
  headline,
  description,
  placeholder,
  ctaLabel,
  form,
}: {
  headline: string
  description?: string
  placeholder: string
  ctaLabel: string
  form: FooterNewsletterFormConfig
}) {
  const { email, setEmail, name, setName, honeypot, setHoneypot, honeypotKey, status, onSubmit } =
    useNewsletterSubmit(form, 'footer')

  // Translucent-on-dark: brand_showcase's main band is always a dark surface,
  // so the fields read as shaded glass (the reference treatment) rather than
  // solid white cards. pl-10 clears the absolutely-positioned leading icon.
  const inputClass =
    'w-full rounded-md border border-white/40 bg-white/15 pl-10 pr-4 py-2.5 text-sm text-white outline-none placeholder:text-[var(--color-on-surface-inverse-muted)] focus:border-white/70'
  const iconClass =
    'pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-on-surface-inverse-muted)]'

  return (
    <div className="max-w-sm" data-chrome-target="footer:newsletter">
      {/* Heading underline bar mirrors BrandShowcaseFooter's ColumnHeading so
          all four footer columns share one heading treatment. */}
      <h3
        className="font-semibold text-base uppercase tracking-wide mb-4"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {headline}
        <span aria-hidden="true" className="block w-12 h-0.5 mt-2 bg-[var(--color-text-primary)]" />
      </h3>
      {description && (
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">{description}</p>
      )}
      {status === 'done' ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          Thanks &mdash; you&rsquo;re on the list.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
          <input
            type="text"
            name={honeypotKey}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="hidden"
          />
          {form.nameFieldKey && (
            <div className="relative">
              <User size={16} strokeWidth={2} aria-hidden="true" className={iconClass} />
              <label htmlFor="newsletter-stacked-name" className="sr-only">
                {form.nameFieldLabel ?? 'First Name'}
              </label>
              <input
                id="newsletter-stacked-name"
                type="text"
                name="name"
                autoComplete="given-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={form.nameFieldLabel ?? 'First Name'}
                className={inputClass}
              />
            </div>
          )}
          <div className="relative">
            <Mail size={16} strokeWidth={2} aria-hidden="true" className={iconClass} />
            <label htmlFor="newsletter-stacked-email" className="sr-only">
              Email address
            </label>
            <input
              id="newsletter-stacked-email"
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className={inputClass}
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full rounded-md px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-[var(--color-cta-text,#fff)] bg-[var(--color-brand-primary,var(--color-cta))] hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {status === 'loading' ? '…' : ctaLabel}
          </button>
        </form>
      )}
    </div>
  )
}

/**
 * The `newsletter_signup` SECTION's client form — same submit pipeline as the
 * footer form above (do not fork a third), different surface: an inline
 * email + primary-CTA row that stacks below `sm`, an in-place success message
 * that replaces the input row, inline (never toast) errors, and a dark-band
 * input treatment driven by `onDark` (the CTA button inverts for free via the
 * scoped `--cta-*` vars the section wrapper sets on dark surfaces).
 */
export function NewsletterBandForm({
  ctaLabel,
  successMessage,
  consentNote,
  placeholder = 'you@email.com',
  onDark = false,
  sourcePageSlug,
  form,
}: {
  ctaLabel: string
  successMessage: string
  consentNote?: string
  placeholder?: string
  onDark?: boolean
  sourcePageSlug?: string
  form: FooterNewsletterFormConfig
}) {
  const { email, setEmail, honeypot, setHoneypot, honeypotKey, status, onSubmit } = useNewsletterSubmit(
    form,
    sourcePageSlug ?? 'newsletter_signup',
  )

  // Dark bands derive the input from the section's on-surface color at
  // reduced opacity (mirrors the dark-band CTA inversion); light surfaces
  // keep the standard bordered white input.
  const inputClass = onDark
    ? 'bg-white/10 border border-white/40 text-[var(--section-on-surface,#fff)] placeholder:text-[var(--color-on-surface-inverse-muted)]'
    : 'bg-white border border-[var(--color-border,#e5e7eb)] text-gray-900 placeholder:text-gray-400'

  if (status === 'done') {
    return (
      <p className={`text-sm ${onDark ? 'text-[var(--color-on-surface-inverse,#fff)]' : 'text-[var(--color-text-primary,#111827)]'}`} role="status">
        {successMessage}
      </p>
    )
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <input
        type="text"
        name={honeypotKey}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
      />
      <div className="flex flex-col sm:flex-row gap-2">
        {/* The placeholder is never the label — a11y hard rule. */}
        <label htmlFor="newsletter-band-email" className="sr-only">
          Email address
        </label>
        <input
          id="newsletter-band-email"
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 min-w-0 px-4 py-2.5 text-sm outline-none rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))] ${inputClass}`}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="cta-primary px-6 py-2.5 text-sm font-semibold rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))] disabled:opacity-60"
        >
          {status === 'loading' ? '…' : ctaLabel}
        </button>
      </div>
      {status === 'error' && (
        <p className={`text-xs ${onDark ? 'text-[var(--color-on-surface-inverse-muted)]' : 'text-[var(--color-text-secondary,#6b7280)]'}`}>
          Something went wrong — please try again.
        </p>
      )}
      {consentNote && (
        <p className={`text-xs ${onDark ? 'text-[var(--color-on-surface-inverse-muted)]' : 'text-[var(--color-text-secondary,#6b7280)]'}`}>
          {consentNote}
        </p>
      )}
    </form>
  )
}
